import styles from './Filters.module.scss';
import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import iconFilter from '../img/filters/iconFilter.svg';
import iconFilterActive from '../img/filters/iconFilterActive.svg';
import { ReactComponent as IconCloseBlue } from '../img/filters/iconCloseBlue.svg';
import { ReactComponent as IconChevron } from '../../../assets/img/filter/iconChevronDown.svg';
import { ReactComponent as IconChevronUp } from '../../../assets/img/filter/iconChevronUp.svg';
import { ReactComponent as IconPlanet } from '../img/filters/iconPlanet.svg';
import { ReactComponent as IconGender } from '../img/filters/iconGender.svg';
import { ReactComponent as IconCalendar } from '../img/filters/iconCalendar.svg';
import { ReactComponent as IconProblem } from '../img/filters/iconProblem.svg';
import { ReactComponent as IconContract } from '../img/filters/iconContract.svg';
import { ReactComponent as IconDone } from '../img/iconDone.svg';
import { ReactComponent as IconCategory } from '../img/filters/iconCategory.svg';
import { Button } from 'ui/Button';
import { KeysFiltersType } from 'redux/Api/employedsApi/type';
import {
  EmployedsAdaptiveFilter,
  SelectedItem,
} from 'components/EmployedsFilters/EmployedsAdaptiveFilter';
import { EmployedsFilterDateInput } from 'components/EmployedsFilters/shared/EmployedsFilterDateInput';
import { DatesRangeValue } from '@mantine/dates';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';

function formatDateForAPI(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface CategoryItem {
  id: string;
  name: string;
}

export interface EmployedsFilterParams {
  genders?: string[];
  issues?: string[];
  categories?: CategoryItem[];
  nationalities?: string[];
  contracts?: string[];
  date_add?: { start: string; end: string };
}

interface PropsType {
  dataFilters: {
    genders: KeysFiltersType[];
    issues: KeysFiltersType[];
    nationalities: KeysFiltersType[];
    contracts: KeysFiltersType[];
    categories: KeysFiltersType[];
  };
  initialFilters?: EmployedsFilterParams;
  onApplyFilters: (filters: EmployedsFilterParams) => void;
  onResetFilters: () => void;
  isLoading?: boolean;
  showFilters?: boolean;
  notShowText?: boolean;
}

export interface AdaptiveFilterOption {
  keyword: string;
  title: string;
}

export const Filters: FC<PropsType> = ({
  dataFilters,
  initialFilters = {},
  onApplyFilters,
  onResetFilters,
  showFilters = false,
  isLoading = false,
  notShowText = false,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const filtersWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideCalendarPopover = (target as Element).closest?.(
        '[class*="mantine-Popover-dropdown"], [class*="mantine-DatePicker"]',
      );
      if (
        filtersWrapperRef.current &&
        !filtersWrapperRef.current.contains(target) &&
        !isInsideCalendarPopover
      ) {
        setIsFiltersOpen(false);
        setOpenSection(null);
      }
    };

    if (isFiltersOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFiltersOpen]);

  useEffect(() => {
    if (!isFiltersOpen) {
      setOpenSection(null);
    }
  }, [isFiltersOpen]);

  const [nationalityKeywords, setNationalityKeywords] = useState<string[]>(
    initialFilters.nationalities ?? [],
  );
  const [contractKeywords, setContractKeywords] = useState<string[]>(
    initialFilters.contracts ?? [],
  );
  const [categoriesKeywords, setCategoriesKeywords] = useState<CategoryItem[]>(
    initialFilters.categories ?? [],
  );
  const [genderKeywords, setGenderKeywords] = useState<string[]>(
    initialFilters.genders ?? [],
  );
  const [issueKeywords, setIssueKeywords] = useState<string[]>(
    initialFilters.issues ?? [],
  );
  const [dateAddRange, setDateAddRange] = useState<DatesRangeValue>([
    initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
    initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
  ]);

  useEffect(() => {
    setNationalityKeywords(initialFilters.nationalities ?? []);
    setCategoriesKeywords(initialFilters.categories ?? []);
    setGenderKeywords(initialFilters.genders ?? []);
    setIssueKeywords(initialFilters.issues ?? []);
    setContractKeywords(initialFilters.contracts ?? []);
    setDateAddRange([
      initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
      initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
    ]);
  }, [
    initialFilters.nationalities,
    initialFilters.categories,
    initialFilters.issues,
    initialFilters.genders,
    initialFilters.contracts,
    initialFilters.date_add?.start,
    initialFilters.date_add?.end,
  ]);

  const toggleSection = (key: string) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  const [queryParamsCategorys, setQueryParamsCategorys] = useState({
    page: 1,
    search: '',
  });

  const handleCategorysServerSearch = useCallback((search: string) => {
    setQueryParamsCategorys((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: categorysFiltersData,
    isLoading: isLoadingCategorys,
    isFetching: isFetchingCategorys,
  } = useGetCategoryEmployedsQuery(
    {
      page: queryParamsCategorys.page,
      ...(queryParamsCategorys.search ? { search: queryParamsCategorys.search } : {}),
    },
    { skip: !isFiltersOpen },
  );

  const hasNextCategoryPage =
    !!categorysFiltersData?.meta &&
    categorysFiltersData.meta.current_page < categorysFiltersData.meta.last_page;

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategorys) return;
    if (!hasNextCategoryPage) return;
    setQueryParamsCategorys((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingCategorys, hasNextCategoryPage]);

  const transformNationalities = (): AdaptiveFilterOption[] => {
    return (dataFilters?.nationalities || []).map((type) => ({
      keyword: type.keyword || '',
      title: type.title,
    }));
  };

  const transformCategories = (): AdaptiveFilterOption[] => {
    return (categorysFiltersData?.data || []).map((type) => ({
      keyword: type.id.toString() || '',
      title: type.name || '',
    }));
  };

  const transformContracts = (): AdaptiveFilterOption[] => {
    return (dataFilters?.contracts || []).map((type) => ({
      keyword: type.keyword || '',
      title: type.title,
    }));
  };

  const transformGenders = (): AdaptiveFilterOption[] => {
    return (dataFilters?.genders || []).map((type) => ({
      keyword: type.keyword || '',
      title: type.title,
    }));
  };

  const transformIssues = (): AdaptiveFilterOption[] => {
    return (dataFilters?.issues || []).map((type) => ({
      keyword: type.keyword || '',
      title: type.title,
    }));
  };

  const hasAppliedFilters = (): boolean => {
    return (
      (initialFilters.nationalities?.length ?? 0) > 0 ||
      (initialFilters.categories?.length ?? 0) > 0 ||
      (initialFilters.genders?.length ?? 0) > 0 ||
      (initialFilters.issues?.length ?? 0) > 0 ||
      (initialFilters.contracts?.length ?? 0) > 0 ||
      initialFilters.date_add?.start != null ||
      initialFilters.date_add?.end != null
    );
  };

  const countAppliedFilters = (): number => {
    let count = 0;
    if (initialFilters.nationalities?.length) count++;
    if (initialFilters.categories?.length) count++;
    if (initialFilters.genders?.length) count++;
    if (initialFilters.issues?.length) count++;
    if (initialFilters.contracts?.length) count++;
    if (initialFilters.date_add?.start != null || initialFilters.date_add?.end != null)
      count++;
    return count;
  };

  const hasFilters = hasAppliedFilters();
  const filtersCount = countAppliedFilters();
  const showText = !notShowText;

  const isNationalityFilterApplied = (): boolean =>
    (nationalityKeywords?.length ?? 0) > 0;
  const isCategoriesFilterApplied = (): boolean => (categoriesKeywords?.length ?? 0) > 0;
  const isContractFilterApplied = (): boolean => (contractKeywords?.length ?? 0) > 0;
  const isDateAddFilterApplied = (): boolean =>
    dateAddRange[0] != null || dateAddRange[1] != null;
  const isGenderFilterApplied = (): boolean => (genderKeywords?.length ?? 0) > 0;
  const isIssueFilterApplied = (): boolean => (issueKeywords?.length ?? 0) > 0;

  const handleCloseFilters = () => {
    setIsFiltersOpen(false);
    setOpenSection(null);
  };

  const handleCategoriesChange = (values: string[] | SelectedItem[]) => {
    setCategoriesKeywords(values as CategoryItem[]);
  };

  const handleNationalityChange = (values: string[] | SelectedItem[]) => {
    setNationalityKeywords(values as string[]);
  };

  const handleGenderChange = (values: string[] | SelectedItem[]) => {
    setGenderKeywords(values as string[]);
  };

  const handleIssueChange = (values: string[] | SelectedItem[]) => {
    setIssueKeywords(values as string[]);
  };

  const handleContractChange = (values: string[] | SelectedItem[]) => {
    setContractKeywords(values as string[]);
  };

  const handleApply = () => {
    const start = dateAddRange[0];
    const end = dateAddRange[1] ?? start;
    const dateAdd =
      start != null && end != null
        ? {
            start: formatDateForAPI(start),
            end: formatDateForAPI(end),
          }
        : undefined;
    onApplyFilters({
      genders: genderKeywords,
      issues: issueKeywords,
      nationalities: nationalityKeywords,
      categories: categoriesKeywords,
      contracts: contractKeywords,
      date_add: dateAdd,
    });
    handleCloseFilters();
  };

  const handleReset = () => {
    if (isLoading) return;
    setNationalityKeywords([]);
    setCategoriesKeywords([]);
    setGenderKeywords([]);
    setIssueKeywords([]);
    setContractKeywords([]);
    setDateAddRange([null, null]);
    onResetFilters();
    handleCloseFilters();
  };

  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      <div ref={filtersWrapperRef} className={styles.filtersWrapper}>
        <button
          className={`${styles.filterButton} ${hasFilters ? styles.active : ''}`}
          onClick={() => setIsFiltersOpen((prev) => !prev)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: showText ? '4px' : undefined,
            }}>
            {(!showText || !hasFilters) && (
              <img
                src={hasFilters ? iconFilterActive : iconFilter}
                alt='Фильтр'
                className={styles.filterIcon}
              />
            )}
            {showText && (
              <span style={{ color: hasFilters ? '#002CFB' : undefined }}>Фильтр</span>
            )}
            {hasFilters && filtersCount > 1 && (
              <div className={styles.filterCountBadge}>{filtersCount}</div>
            )}
            {hasFilters && (
              <IconCloseBlue
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleReset();
                }}
              />
            )}
          </div>
        </button>
        {isFiltersOpen && (
          <div className={styles.filtersDropdown} ref={filtersRef}>
            <span className={styles.filtersTitle}>Фильтр</span>
            <div className={styles.sections}>
              <div
                className={`${styles.section} ${
                  openSection === 'serviceType' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('serviceType')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconPlanet className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Гражданство</span>
                      {isNationalityFilterApplied() && (
                        <span className={styles.activeDot} />
                      )}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'serviceType' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'serviceType' && (
                  <div className={styles.sectionBody}>
                    <EmployedsAdaptiveFilter
                      options={transformNationalities()}
                      selectedValues={nationalityKeywords}
                      onChange={handleNationalityChange}
                      searchPlaceholder='Искать...'
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'categoriesType' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('categoriesType')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconCategory className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Категории исполнителей</span>
                      {isCategoriesFilterApplied() && (
                        <span className={styles.activeDot} />
                      )}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'categoriesType' ? (
                      <IconChevronUp />
                    ) : (
                      <IconChevron />
                    )}
                  </span>
                </button>

                {openSection === 'categoriesType' && (
                  <div className={styles.sectionBody}>
                    <EmployedsAdaptiveFilter
                      options={transformCategories()}
                      selectedValues={categoriesKeywords}
                      onChange={handleCategoriesChange}
                      searchPlaceholder='Искать...'
                      serverSearch={true}
                      onServerSearchChange={handleCategorysServerSearch}
                      isFetchingTypes={isFetchingCategorys}
                      hasNextPage={hasNextCategoryPage}
                      isFetchingMore={isFetchingCategorys}
                      onLoadMore={loadMoreCategories}
                      storeFullObject={true}
                      isLoadingCategorys={isLoadingCategorys}
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'status' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('status')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconCalendar className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Дата добавления</span>
                      {isDateAddFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'status' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'status' && (
                  <div className={styles.sectionBody}>
                    <EmployedsFilterDateInput
                      type='range'
                      value={dateAddRange}
                      onChange={(value) => {
                        const range: DatesRangeValue = Array.isArray(value)
                          ? [value[0] ?? null, value[1] ?? null]
                          : [value as Date | null, null];
                        setDateAddRange(range);
                      }}
                      placeholder='Выбери период'
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'amount' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('amount')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconGender className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Пол</span>
                      {isGenderFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'amount' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'amount' && (
                  <div className={styles.sectionBody}>
                    <EmployedsAdaptiveFilter
                      options={transformGenders()}
                      selectedValues={genderKeywords}
                      onChange={handleGenderChange}
                      singleSelect
                      showSearch={false}
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'contractType' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('contractType')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconContract className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Наличие договора</span>
                      {isContractFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'contractType' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'contractType' && (
                  <div className={styles.sectionBody}>
                    <EmployedsAdaptiveFilter
                      showSearch={false}
                      options={transformContracts()}
                      selectedValues={contractKeywords}
                      onChange={handleContractChange}
                      singleSelect
                    />
                  </div>
                )}
              </div>

              {!showFilters && (
                <div
                  className={`${styles.section} ${
                    openSection === 'executor' ? styles.sectionActive : ''
                  }`}>
                  <button
                    type='button'
                    className={styles.sectionHeader}
                    onClick={() => toggleSection('executor')}>
                    <div className={styles.sectionHeaderContent}>
                      <IconProblem className={styles.sectionIcon} />
                      <div className={styles.textActions}>
                        <span>Проблема</span>
                        {isIssueFilterApplied() && <span className={styles.activeDot} />}
                      </div>
                    </div>
                    <span className={styles.sectionArrow}>
                      {openSection === 'executor' ? <IconChevronUp /> : <IconChevron />}
                    </span>
                  </button>

                  {openSection === 'executor' && (
                    <div className={styles.sectionBody}>
                      <EmployedsAdaptiveFilter
                        options={transformIssues()}
                        selectedValues={issueKeywords}
                        onChange={handleIssueChange}
                        showSearch={false}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <Button
                className={styles.resetButton}
                theme='outline'
                onClick={handleReset}>
                <span
                  style={{
                    marginRight: '6px',
                  }}>
                  Сбросить
                </span>
              </Button>
              <Button className={styles.btnDone} onClick={handleApply} theme='filed'>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: '6px',
                  }}>
                  <span>Применить</span> <IconDone style={{ width: 20, height: 20 }} />
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
